import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { imgUrl, formatPrice } from "../lib/api";
import { productName } from "../lib/i18n";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal,
          refreshStock, hasSoldOut } = useCart();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  // Səbət hər açılanda stok serverdən yenilənir — məhsul bu arada satıla bilər.
  useEffect(() => {
    if (open) refreshStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-brand-bg border-l border-brand-line p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-6 border-b border-brand-line">
          <SheetTitle className="font-serif text-2xl tracking-wide text-left flex items-center gap-2">
            {t("cart")} {items.length > 0 && <span className="text-brand-muted text-base">({items.length})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-brand-muted gap-4">
            <ShoppingBag size={40} strokeWidth={1} />
            <p className="text-sm">{t("cart_empty")}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              {items.map((it) => (
                <div key={it.key} className="flex gap-4" data-testid={`cart-item-${it.id}`}>
                  <div className="w-20 h-24 bg-brand-bg2 overflow-hidden shrink-0 relative">
                    <img src={imgUrl(it.image)} alt={it.name}
                      className={`w-full h-full object-cover ${(it.stock ?? 0) <= 0 ? "opacity-40 grayscale" : ""}`}
                      onError={(e) => { e.target.src = imgUrl(null); }} />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between gap-2">
                      <p className="font-serif text-lg leading-tight">{lang === "en" ? it.name_en || it.name : it.name}</p>
                      <button onClick={() => removeItem(it.key)} data-testid={`cart-remove-${it.id}`} className="text-brand-muted hover:text-brand-ink">
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-xs text-brand-muted mt-1">
                      {it.code}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""}
                    </p>
                    {(it.stock ?? 0) <= 0 ? (
                      /* Məhsul bu arada satılıb — miqdar dəyişmək mənasızdır,
                         yalnız səbətdən silmək təklif olunur. */
                      <div className="mt-auto pt-3 flex justify-between items-center gap-3">
                        <span
                          className="text-xs tracking-wider uppercase border border-destructive text-destructive px-2 py-1"
                          data-testid={`cart-sold-${it.id}`}
                        >
                          {t("sold_out_badge")}
                        </span>
                        <button
                          onClick={() => removeItem(it.key)}
                          data-testid={`cart-remove-sold-${it.id}`}
                          className="flex items-center gap-2 text-xs tracking-wider uppercase text-brand-sub hover:text-brand-ink border border-brand-line px-3 py-1.5"
                        >
                          <Trash2 size={13} strokeWidth={1.5} /> {t("remove")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mt-auto pt-3">
                          <div className="flex items-center border border-brand-line">
                            <button onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2 hover:bg-brand-bg2" data-testid={`cart-dec-${it.id}`}>
                              <Minus size={13} strokeWidth={1.5} />
                            </button>
                            <span className="px-3 text-sm">{it.quantity}</span>
                            <button
                              onClick={() => updateQty(it.key, it.quantity + 1)}
                              disabled={it.quantity >= it.stock}
                              className="p-2 hover:bg-brand-bg2 disabled:opacity-30 disabled:cursor-not-allowed"
                              data-testid={`cart-inc-${it.id}`}
                            >
                              <Plus size={13} strokeWidth={1.5} />
                            </button>
                          </div>
                          <p className="text-sm tracking-wider">{formatPrice(it.price * it.quantity)}</p>
                        </div>
                        {it.quantity >= it.stock && (
                          <p className="text-xs text-brand-muted mt-2" data-testid={`cart-max-${it.id}`}>
                            {t("max_stock_reached")} ({it.stock})
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-brand-line px-6 py-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="overline">{t("subtotal")}</span>
                <span className="font-serif text-2xl" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
              </div>
              {hasSoldOut && (
                <p className="text-xs text-destructive" data-testid="cart-sold-warning">
                  {t("remove_sold_to_continue")}
                </p>
              )}
              <button
                data-testid="cart-checkout-btn"
                onClick={goCheckout}
                disabled={hasSoldOut}
                className="w-full bg-brand-ink text-white py-4 text-sm tracking-[0.15em] uppercase hover:bg-brand-oxid transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("checkout")}
              </button>
              <button onClick={() => setOpen(false)} className="w-full text-center text-xs text-brand-muted hover:text-brand-ink tracking-wider uppercase">
                {t("continue_shopping")}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
